import { singleline } from "@core/foundation/string";
import { mcpApi } from "@core/ports/mcp";
import { z } from "@core/zod";

import { PROJECT_DOMAIN } from "@platform/core";

import { TAXONOMY_SERVICE } from "../config/index.ts";
import {
  FolderStructureRevision,
  FolderStructureRevisionIdSchema,
  Tag,
  TagKind,
  TagKindIdSchema,
  Taxonomy,
  TaxonomyIdSchema,
} from "../models/index.ts";

const MAX_TAGS_PER_CALL = 50;

export const taxonomyMcpApi = mcpApi(
  {
    domain: `mcp.${TAXONOMY_SERVICE.LABEL}.${PROJECT_DOMAIN}`,
    serverInfo: { name: TAXONOMY_SERVICE.LABEL, version: "0.1.0" },
  },
  {
    add_tag_kind: {
      description: singleline`
        Adds a tag kind to one of your taxonomies, with its first tags, and
        returns the kind and the tags, each with the permanent id to classify
        by.
      `,
      inputSchema: z.object(
        {
          name: TagKind.shape.name,
          tagNames: z.array(Tag.shape.name).max(MAX_TAGS_PER_CALL),
          taxonomyId: TaxonomyIdSchema,
        },
        {
          name: TagKind.descriptions.name,
          tagNames: "The names of the kind's first tags, at most 50, or none",
          taxonomyId: "The id of the taxonomy to add the kind to",
        },
      ),
      resultSchema: z.object(
        { tagKind: TagKind.schema, tags: z.array(Tag.schema) },
        {
          tagKind: "The tag kind added",
          tags: "The tags added to the kind, in the order given",
        },
      ),
    },
    add_tags: {
      description: singleline`
        Adds tags to a tag kind of one of your taxonomies and returns them, each
        with the permanent id to classify by.
      `,
      inputSchema: z.object(
        {
          names: z.array(Tag.shape.name).nonempty().max(MAX_TAGS_PER_CALL),
          tagKindId: TagKindIdSchema,
          taxonomyId: TaxonomyIdSchema,
        },
        {
          names: "The names of the tags to add, one to 50",
          tagKindId: "The id of the tag kind to add the tags to",
          taxonomyId: "The id of the taxonomy holding the tag kind",
        },
      ),
      resultSchema: z.object(
        { tags: z.array(Tag.schema) },
        { tags: "The tags added, in the order given" },
      ),
    },
    get_folder_structure: {
      description: singleline`
        Returns the folder structure of one of your taxonomies, to change and
        pass back to set_folder_structure.
      `,
      inputSchema: z.object(
        { taxonomyId: TaxonomyIdSchema },
        { taxonomyId: "The id of the taxonomy" },
      ),
      resultSchema: z.object(
        { folderStructure: Taxonomy.shape.folderStructure },
        { folderStructure: Taxonomy.descriptions.folderStructure },
      ),
    },
    get_taxonomy: {
      description: singleline`
        Returns one of your taxonomies with its tag kinds. list_tags lists a
        kind's tags.
      `,
      inputSchema: z.object(
        { taxonomyId: TaxonomyIdSchema },
        { taxonomyId: "The id of the taxonomy" },
      ),
      resultSchema: z.object(
        { tagKinds: z.array(TagKind.schema), taxonomy: Taxonomy.schema },
        { tagKinds: "The taxonomy's tag kinds", taxonomy: "The taxonomy" },
      ),
    },
    list_folder_structure_revisions: {
      description: singleline`
        Lists the saved revisions of one of your taxonomy's folder structure,
        newest first, each restorable through restore_folder_structure.
      `,
      inputSchema: z.object(
        { taxonomyId: TaxonomyIdSchema },
        { taxonomyId: "The id of the taxonomy" },
      ),
      resultSchema: z.object(
        { revisions: z.array(FolderStructureRevision.schema) },
        { revisions: "The folder structure's revisions, newest first" },
      ),
    },
    list_tags: {
      description: singleline`
        Lists the tags of a tag kind of one of your taxonomies, each with the
        permanent id to classify by.
      `,
      inputSchema: z.object(
        { tagKindId: TagKindIdSchema, taxonomyId: TaxonomyIdSchema },
        {
          tagKindId: "The id of the tag kind whose tags to list",
          taxonomyId: "The id of the taxonomy holding the tag kind",
        },
      ),
      resultSchema: z.object(
        { tags: z.array(Tag.schema) },
        { tags: "The kind's tags" },
      ),
    },
    restore_folder_structure: {
      description: singleline`
        Restores an earlier revision of one of your taxonomy's folder structure,
        saving it as the newest revision.
      `,
      inputSchema: z.object(
        {
          revisionId: FolderStructureRevisionIdSchema,
          taxonomyId: TaxonomyIdSchema,
        },
        {
          revisionId: "The id of the revision to restore",
          taxonomyId: "The id of the taxonomy",
        },
      ),
      resultSchema: z.object(
        { revision: FolderStructureRevision.schema },
        { revision: "The new revision holding the restored structure" },
      ),
    },
    set_folder_structure: {
      description: singleline`
        Replaces the folder structure of one of your taxonomies and saves it as
        a revision. A folder holds a name, conditions keyed by tag kind id, and
        its subfolders: {"kind": "equal", "tagId": ...} keeps the records
        carrying that tag of the kind, and {"kind": "expansion"} turns the
        folder into one subfolder per tag of the kind, at most one expansion per
        folder. Every folder inherits the tags picked above it. Refuses a
        condition naming a tag kind or tag the taxonomy does not hold.
      `,
      inputSchema: z.object(
        {
          folderStructure: Taxonomy.shape.folderStructure,
          taxonomyId: TaxonomyIdSchema,
        },
        {
          folderStructure: "The taxonomy's new folder structure",
          taxonomyId: "The id of the taxonomy",
        },
      ),
      resultSchema: z.object(
        { revision: FolderStructureRevision.schema },
        { revision: "The revision saving the new structure" },
      ),
    },
  },
);
